// src/components/Theaters.js
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Modal from "react-bootstrap/Modal";
import { Helmet } from "react-helmet";
import {
  ArrowLeft,
  ArrowRight,
  Calendar as CalendarIcon,
  Utensils,
  Play,
  MapPin,
  Users,
  Check,
  X,
  Sparkles,
  Film,
} from "lucide-react";
import { URLS } from "../Url";
import { mediaSrc } from "../utils/media";
import Header from "./Header";
import Footer from "./Footer";


// Production time-slot schedules for each individual theatre across all locations
const DEFAULT_THEATRE_SLOTS = {
  // Gachibowli
  "Royal Theatre": [
    { fromTime: "09:30", toTime: "12:30" },
    { fromTime: "13:00", toTime: "16:00" },
    { fromTime: "16:30", toTime: "18:00" },
    { fromTime: "18:30", toTime: "21:30" },
    { fromTime: "22:00", toTime: "00:30" },
  ],
  "Premier Theatre": [
    { fromTime: "09:00", toTime: "11:30" },
    { fromTime: "12:00", toTime: "15:00" },
    { fromTime: "15:30", toTime: "17:00" },
    { fromTime: "17:30", toTime: "20:30" },
    { fromTime: "21:30", toTime: "00:30" },
  ],
  "Bloom Theatre": [
    { fromTime: "09:30", toTime: "12:30" },
    { fromTime: "13:00", toTime: "16:00" },
    { fromTime: "16:30", toTime: "18:00" },
    { fromTime: "18:30", toTime: "21:30" },
    { fromTime: "22:00", toTime: "00:30" },
  ],
  "Galaxy Theatre": [
    { fromTime: "09:30", toTime: "12:30" },
    { fromTime: "13:00", toTime: "16:00" },
    { fromTime: "16:30", toTime: "18:00" },
    { fromTime: "18:30", toTime: "21:30" },
    { fromTime: "22:00", toTime: "00:30" },
  ],
  "Radiant Theatre": [
    { fromTime: "09:30", toTime: "12:30" },
    { fromTime: "13:00", toTime: "16:00" },
    { fromTime: "16:30", toTime: "18:00" },
    { fromTime: "18:30", toTime: "21:30" },
    { fromTime: "22:00", toTime: "00:30" },
  ],
  "Couple Cove Theatre": [
    { fromTime: "09:00", toTime: "11:30" },
    { fromTime: "12:00", toTime: "15:00" },
    { fromTime: "15:30", toTime: "17:00" },
    { fromTime: "17:30", toTime: "20:30" },
    { fromTime: "21:30", toTime: "00:30" },
  ],

  // Kondapur - Premium Theatres
  "Iris Theatre": [
    { fromTime: "09:30", toTime: "12:30" },
    { fromTime: "13:00", toTime: "16:00" },
    { fromTime: "16:30", toTime: "18:00" },
    { fromTime: "18:30", toTime: "21:30" },
    { fromTime: "22:00", toTime: "00:30" },
  ],
  "Carnival Den": [
    { fromTime: "10:00", toTime: "13:00" },
    { fromTime: "13:30", toTime: "16:30" },
    { fromTime: "17:00", toTime: "18:30" },
    { fromTime: "19:00", toTime: "22:00" },
    { fromTime: "22:30", toTime: "01:00" },
  ],
  "Amora Theatre": [
    { fromTime: "09:00", toTime: "12:00" },
    { fromTime: "12:30", toTime: "15:30" },
    { fromTime: "16:00", toTime: "17:30" },
    { fromTime: "18:00", toTime: "21:00" },
    { fromTime: "21:30", toTime: "00:30" },
  ],
  "Vibe Theatres": [
    { fromTime: "09:00", toTime: "10:30" },
    { fromTime: "11:00", toTime: "12:30" },
    { fromTime: "13:00", toTime: "14:30" },
    { fromTime: "15:00", toTime: "16:30" },
    { fromTime: "17:00", toTime: "18:30" },
    { fromTime: "19:00", toTime: "20:30" },
    { fromTime: "21:00", toTime: "22:30" },
    { fromTime: "23:00", toTime: "00:30" },
  ],
  "Ruby Theatre": [
    { fromTime: "09:00", toTime: "11:30" },
    { fromTime: "12:00", toTime: "15:00" },
    { fromTime: "15:30", toTime: "17:00" },
    { fromTime: "17:30", toTime: "20:30" },
    { fromTime: "21:30", toTime: "00:30" },
  ],
  "Joy Theatre": [
    { fromTime: "10:00", toTime: "13:00" },
    { fromTime: "13:30", toTime: "16:30" },
    { fromTime: "17:00", toTime: "18:30" },
    { fromTime: "19:00", toTime: "22:00" },
    { fromTime: "22:30", toTime: "01:00" },
  ],

  // Miyapur
  "Cadbury": [
    { fromTime: "10:00", toTime: "11:30" },
    { fromTime: "12:30", toTime: "15:30" },
    { fromTime: "16:00", toTime: "17:30" },
    { fromTime: "18:00", toTime: "21:00" },
    { fromTime: "21:30", toTime: "00:30" },
  ],
  "Lyra": [
    { fromTime: "10:00", toTime: "11:30" },
    { fromTime: "12:00", toTime: "15:00" },
    { fromTime: "15:30", toTime: "17:00" },
    { fromTime: "17:30", toTime: "20:30" },
    { fromTime: "21:30", toTime: "00:30" },
  ],
  "Lilac Theatre": [
    { fromTime: "10:30", toTime: "12:00" },
    { fromTime: "12:30", toTime: "15:30" },
    { fromTime: "16:00", toTime: "17:30" },
    { fromTime: "18:00", toTime: "21:00" },
    { fromTime: "22:00", toTime: "00:30" },
  ],
};

export default function Theaters() {
  const { slug } = useParams();
  const locationState = useLocation();
  const navigate = useNavigate();

  const [theaters, setTheaters] = useState([]);
  const [allTheatersByLocation, setAllTheatersByLocation] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [location, setLocation] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState({});
  const [activeshow, setActiveshow] = useState([]);
  const [activeSlot, setActiveSlot] = useState(null);
  const [nintymin, setnintymin] = useState(0);
  const [modalPop, setModalPop] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [embedUrl, setEmbedUrl] = useState("");

  const getTodayDateString = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const [date, setDate] = useState(getTodayDateString());
  const dateRef = useRef(null);

  const formatDate = (dateString) => {
    const dateObj = new Date(dateString);
    const dd = dateObj.getDate().toString().padStart(2, "0");
    const mm = (dateObj.getMonth() + 1).toString().padStart(2, "0");
    const yyyy = dateObj.getFullYear();
    return `${yyyy}-${mm}-${dd}`;
  };

  const formattedDateString = formatDate(date);

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const day = d.getDate();
    const months = [
      "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
      "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"
    ];
    const mon = months[d.getMonth()];
    const yr = d.getFullYear();
    return `${day} ${mon} ${yr}`;
  };

  // Helper to match address against route slug
  const isAddressMatch = useCallback((addr, s) => {
    if (!addr?.name || !s) return false;
    const lowerName = addr.name.toLowerCase();
    const lowerSlug = s.toLowerCase();
    if (lowerSlug.includes("kondapur") && lowerName.includes("kondapur")) return true;
    if (lowerSlug.includes("gachibowli") && lowerName.includes("gachibowli")) return true;
    if (lowerSlug.includes("miyapur") && lowerName.includes("miyapur")) return true;
    return lowerName.replace(/\s+/g, "-") === lowerSlug;
  }, []);

  // Initial address fetch
  useEffect(() => {
    const fetchAllAddresses = async () => {
      try {
        const res = await axios.get(
          "https://api.carnivalcastle.com/v1/carnivalApi/admin/address/alladdress"
        );
        if (res.data?.success) {
          setAddresses(res.data.data || []);
        }
      } catch (err) {
        console.error("Failed to load addresses", err);
      }
    };
    fetchAllAddresses();
  }, []);

  // Location detection and data fetching
  useEffect(() => {
    const fetchLocation = async () => {
      const stateAddr = locationState.state?.address;
      if (stateAddr && isAddressMatch(stateAddr, slug)) {
        setLocation(stateAddr);
        sessionStorage.setItem("selectedAddress", JSON.stringify(stateAddr));
        await fetchTheatersByAddressId(stateAddr._id, date);
        await fetchAllTheatersGroupedByLocation(date);
      } else {
        try {
          const res = await axios.get(
            "https://api.carnivalcastle.com/v1/carnivalApi/admin/address/alladdress"
          );
          setAddresses(res.data.data || []);
          if (res.data?.success) {
            const matched = res.data.data.find((addr) => isAddressMatch(addr, slug));
            if (matched) {
              setLocation(matched);
              sessionStorage.setItem("selectedAddress", JSON.stringify(matched));
              await fetchTheatersByAddressId(matched._id, date);
              await fetchAllTheatersGroupedByLocation(date);
            } else {
              toast.error("Location not found");
              navigate("/locations");
            }
          }
        } catch (err) {
          console.error("Failed to load location", err);
          navigate("/locations");
        }
      }
    };

    if (slug) {
      fetchLocation();
    }
  }, [slug, date, isAddressMatch, navigate]);

  // Clean booking storage on fresh page mount
  useEffect(() => {
    const itemsToRemove = [
      "bookingid", "specialPersonName", "TotalPrice", "TotalPrice2", "addons",
      "addonsData", "adonsJSON", "theaterName", "theaterId",
      "subtotal", "slot", "selectedOccasion", "planType", "paymentkey",
      "orderId", "occprice", "occasionName", "occasion", "invoicePath",
      "extraPersonprice", "extraPersonperprice", "extraAddedPersonsForTheatre",
      "date", "data", "coupondis", "cakeprice", "advancePayment", "countPeople",
      "theaterPrice", "theatrePrices", "comboAdvancePayment", "maxPeople"
    ];
    itemsToRemove.forEach((item) => sessionStorage.removeItem(item));
    sessionStorage.setItem("date", formattedDateString);
  }, [formattedDateString]);

  // Fetch GST / Advance charges
  useEffect(() => {
    axios
      .post(URLS.GetCharges, {})
      .then((res) => {
        if (res.status === 200 && res.data?.charges?.advancePayment) {
          sessionStorage.setItem("advancePayment", res.data.charges.advancePayment);
        }
      })
      .catch((err) => console.error("Error fetching charges:", err));
  }, []);

  const fetchTheatersByAddressId = async (addressId, selectedDate = date) => {
    setIsLoading(true);
    try {
      if (!addressId) return;
      const formatted = formatDate(selectedDate);
      const res = await axios.post(
        "https://api.carnivalcastle.com/v1/carnivalApi/web/getalltheatres/forweb",
        { slotDate: formatted }
      );
      if (res.data?.success) {
        const filteredTheaters = res.data.theatres.filter((theater) => {
          const theaterAddressId =
            typeof theater.address === "string"
              ? theater.address
              : theater.address?._id;
          return theaterAddressId?.toString() === addressId?.toString();
        });

        const now = new Date();
        const processedTheaters = filteredTheaters.map((theater) => {
          const rawSlots =
            theater.availableSlots && theater.availableSlots.length > 0
              ? theater.availableSlots
              : (DEFAULT_THEATRE_SLOTS[theater.name] || []);

          const processedSlots = rawSlots.map((slot) => {
            const [slotHours, slotMinutes] = slot.fromTime.split(":").map(Number);
            const [endHours, endMinutes] = slot.toTime.split(":").map(Number);
            const slotStart = new Date(selectedDate);
            slotStart.setHours(slotHours, slotMinutes, 0, 0);
            const slotEnd = new Date(selectedDate);
            slotEnd.setHours(endHours, endMinutes, 0, 0);
            if (endHours < slotHours) {
              // Late-night slot crossing midnight into the next morning
              slotEnd.setDate(slotEnd.getDate() + 1);
            }
            const currentSelectedDate = new Date(selectedDate);
            const isToday =
              currentSelectedDate.getFullYear() === now.getFullYear() &&
              currentSelectedDate.getMonth() === now.getMonth() &&
              currentSelectedDate.getDate() === now.getDate();
            const isBooked = Boolean(slot.isBooked);
            const isPast = isToday && now > slotEnd;
            const isAlmostOver = isToday && !isPast && slotEnd - now <= 10 * 60 * 1000;
            const isDisabled = isBooked || isPast || isAlmostOver;
            return { ...slot, isBooked, isPast, isAlmostOver, isDisabled };
          });
          const availableCount = processedSlots.filter((s) => !s.isDisabled).length;
          return {
            ...theater,
            availableSlots: processedSlots,
            availableSlotsCount: availableCount,
          };
        });

        setTheaters(processedTheaters);
      } else {
        setTheaters([]);
      }
    } catch (error) {
      console.error("Error fetching theaters:", error);
      setTheaters([]);
      toast.error("Failed to fetch theaters.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllTheatersGroupedByLocation = async (selectedDate = date) => {
    try {
      const formatted = formatDate(selectedDate);
      const res = await axios.post(
        "https://api.carnivalcastle.com/v1/carnivalApi/web/getalltheatres/forweb",
        { slotDate: formatted }
      );
      if (res.data?.success) {
        const now = new Date();
        const processedTheaters = res.data.theatres.map((theater) => {
          const rawSlots =
            theater.availableSlots && theater.availableSlots.length > 0
              ? theater.availableSlots
              : (DEFAULT_THEATRE_SLOTS[theater.name] || []);

          const processedSlots = rawSlots.map((slot) => {
            const [slotHours, slotMinutes] = slot.fromTime.split(":").map(Number);
            const [endHours, endMinutes] = slot.toTime.split(":").map(Number);
            const slotStart = new Date(selectedDate);
            slotStart.setHours(slotHours, slotMinutes, 0, 0);
            const slotEnd = new Date(selectedDate);
            slotEnd.setHours(endHours, endMinutes, 0, 0);
            if (endHours < slotHours) {
              // Late-night slot crossing midnight into the next morning
              slotEnd.setDate(slotEnd.getDate() + 1);
            }
            const currentSelectedDate = new Date(selectedDate);
            const isToday =
              currentSelectedDate.getFullYear() === now.getFullYear() &&
              currentSelectedDate.getMonth() === now.getMonth() &&
              currentSelectedDate.getDate() === now.getDate();
            const isBooked = Boolean(slot.isBooked);
            const isPast = isToday && now > slotEnd;
            const isAlmostOver = isToday && !isPast && slotEnd - now <= 10 * 60 * 1000;
            const isDisabled = isBooked || isPast || isAlmostOver;
            return { ...slot, isBooked, isPast, isAlmostOver, isDisabled };
          });
          const availableCount = processedSlots.filter((s) => !s.isDisabled).length;
          return {
            ...theater,
            availableSlots: processedSlots,
            availableSlotsCount: availableCount,
          };
        });

        const grouped = {};
        processedTheaters.forEach((theater) => {
          const addrId =
            typeof theater.address === "string"
              ? theater.address
              : theater.address?._id;
          if (!grouped[addrId]) {
            const addressObj = addresses.find((addr) => addr._id === addrId) || theater.address;
            grouped[addrId] = { addressObj, theaters: [] };
          }
          grouped[addrId].theaters.push(theater);
        });
        setAllTheatersByLocation(grouped);
      }
    } catch (error) {
      console.error("Error fetching all theaters:", error);
    }
  };

  const handleDateChange = async (e) => {
    const selectedDate = e.target.value;
    if (!selectedDate) return;
    const selectedDateObj = new Date(selectedDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (selectedDateObj < today) {
      toast.error("Please select today's or a future date");
      return;
    }
    setDate(selectedDate);
    const formatted = formatDate(selectedDate);
    sessionStorage.setItem("date", formatted);
    if (location?._id) {
      await fetchTheatersByAddressId(location._id, selectedDate);
      await fetchAllTheatersGroupedByLocation(selectedDate);
    }
  };

  const openCalendar = () => {
    if (dateRef.current) {
      if (dateRef.current.showPicker) {
        dateRef.current.showPicker();
      } else {
        dateRef.current.focus();
      }
    }
  };

  const convertTo12HourFormat = (time24) => {
    if (!time24) return "";
    const [hoursStr, minutes] = time24.split(":");
    const hours = parseInt(hoursStr, 10);
    const hours12 = hours % 12 === 0 ? 12 : hours % 12;
    const period = hours < 12 ? "AM" : "PM";
    return `${hours12}:${minutes.padStart(2, "0")} ${period}`;
  };

  const calculateDuration = (fromTime, toTime) => {
    const [fromH, fromM] = fromTime.split(":").map(Number);
    const [toH, toM] = toTime.split(":").map(Number);
    let start = fromH * 60 + fromM;
    let end = toH * 60 + toM;
    if (end <= start) end += 24 * 60;
    const diffMinutes = end - start;
    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;
    return `${hours}:${minutes === 0 ? "00" : minutes} hr`;
  };

  const calculateSlotDuration = (fromTime, toTime) => {
    const fromDate = new Date(`1970-01-01T${fromTime}:00`);
    const toDate = new Date(`1970-01-01T${toTime}:00`);
    return (toDate - fromDate) / (1000 * 60);
  };

  // Determine if a slot qualifies for Free Add-On (Morning & Afternoon daytime slots)
  const slotHasFreeAddon = (slot) => {
    if (!slot) return false;
    if (slot.freeAddon !== undefined) return Boolean(slot.freeAddon);
    if (slot.hasFreeAddon !== undefined) return Boolean(slot.hasFreeAddon);
    if (!slot.fromTime) return false;
    const [hours] = slot.fromTime.split(":").map(Number);
    // Morning & Afternoon slots: starts between 06:00 and 16:59 (before 5:00 PM)
    return hours >= 6 && hours < 17;
  };

  const handleSlot = (e, data, index) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!data.isDisabled) {
      setSelectedSlot((prevState) => ({ ...prevState, [index]: data }));
    }
    setActiveshow(data);
    setActiveSlot(data);
    const fromTime12 = convertTo12HourFormat(data.fromTime);
    const toTime12 = convertTo12HourFormat(data.toTime);
    sessionStorage.setItem("slot", `${fromTime12} - ${toTime12}`);
    sessionStorage.setItem("hasFreeAddon", slotHasFreeAddon(data) ? "true" : "false");
    const durationInMinutes = calculateSlotDuration(data.fromTime, data.toTime);
    setnintymin(durationInMinutes || 0);
    sessionStorage.setItem("nintymin", durationInMinutes || 0);
    if (durationInMinutes === 90) {
      setModalPop(true);
    }
  };

  const handleBasicPlan = (data, i) => {
    let slotChosen = selectedSlot[i];
    if (!slotChosen) {
      // Auto-select first available non-disabled slot
      const firstAvail = data.availableSlots?.find((s) => !s.isDisabled);
      if (firstAvail) {
        const duration = calculateDuration(firstAvail.fromTime, firstAvail.toTime);
        handleSlot({ preventDefault: () => {} }, { ...firstAvail, duration }, i);
        slotChosen = { ...firstAvail, duration };
      }
    }

    const price =
      slotChosen && slotChosen.duration === "1:30 hr"
        ? data.oneandhalfslotPrice || data.offerPrice
        : data.offerPrice;

    sessionStorage.setItem("theaterId", data._id);
    sessionStorage.setItem("maxPeople", data.maxPeople);
    sessionStorage.setItem(
      "extraPersonprice",
      slotChosen && slotChosen.duration === "1:30 hr"
        ? data.onehalfanhourExtraPersonPrice || 0
        : data.extraPersonprice
    );
    sessionStorage.setItem("theaterName", data.name);
    sessionStorage.setItem("theatermaxSeating", data.maxSeating);
    sessionStorage.setItem("theaterPrice", price);
    sessionStorage.setItem("theatrePrices", price);
    sessionStorage.setItem("TotalPrice", price);
    sessionStorage.setItem("subtotal", price);
    sessionStorage.setItem("cartCakes", JSON.stringify([]));
    sessionStorage.setItem("selectedOccasion", JSON.stringify([]));
    sessionStorage.setItem("occprice", "0");
    sessionStorage.setItem("cakeprice", "0");
    sessionStorage.setItem("cartcakeslength", "500");
    sessionStorage.setItem("addons", "0");
    sessionStorage.setItem("coupondis", "0");
    sessionStorage.setItem("planType", "normal");
    sessionStorage.setItem(
      "hasFreeAddon",
      slotChosen && slotHasFreeAddon(slotChosen) ? "true" : "false"
    );
    navigate("/Basicplan");
  };

  // Video Preview Modal
  const extractYouTubeID = (url) => {
    if (!url) return null;
    const regExp =
      /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const match = url.match(regExp);
    return match ? match[1] : null;
  };

  const handleOpenModal = (url) => {
    const videoId = extractYouTubeID(url);
    if (videoId) {
      setEmbedUrl(`https://www.youtube.com/embed/${videoId}?autoplay=1`);
      setShowModal(true);
    } else if (url) {
      window.open(url, "_blank");
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEmbedUrl("");
  };

  const formatLocationTitle = (name) => {
    if (!name) return "";
    return name.replace(/\s*-\s*Premium Theatres/i, "").trim();
  };

  const getImageSrc = (data) => {
    if (data?.image && Array.isArray(data.image) && data.image[0]) {
      return mediaSrc(data.image[0]);
    }
    return "/img/about.jpg";
  };

  // ── Sorted Theatres: STRICTLY LOWEST PRICE → HIGHEST PRICE ──
  const sortedTheaters = useMemo(() => {
    return [...theaters].sort((a, b) => (a.offerPrice || 0) - (b.offerPrice || 0));
  }, [theaters]);

  const { minPrice, midPrice, maxPrice } = useMemo(() => {
    if (sortedTheaters.length === 0) {
      return { minPrice: 1249, midPrice: 1799, maxPrice: 2799 };
    }
    const min = sortedTheaters[0]?.offerPrice || 1249;
    const max = sortedTheaters[sortedTheaters.length - 1]?.offerPrice || 2799;
    const midIdx = Math.floor(sortedTheaters.length / 2);
    const mid = sortedTheaters[midIdx]?.offerPrice || 1799;
    return { minPrice: min, midPrice: mid, maxPrice: max };
  }, [sortedTheaters]);

  // Compute tier based on relative ascending price position
  const getTheatreTier = (idx, total) => {
    const num = (idx + 1).toString().padStart(2, "0");
    if (total <= 3) {
      if (idx === 0) return { number: num, tier: "ENTRY" };
      if (idx === 1) return { number: num, tier: "SIGNATURE" };
      return { number: num, tier: "PREMIUM" };
    }
    const ratio = idx / (total - 1);
    if (ratio <= 0.34) return { number: num, tier: "ENTRY" };
    if (ratio <= 0.67) return { number: num, tier: "SIGNATURE" };
    return { number: num, tier: "PREMIUM" };
  };

  if (isLoading) {
    return (
      <div className="th-loading-screen">
        <div className="th-spinner" />
        <p>Curating private theatres...</p>
      </div>
    );
  }

  if (!location) {
    return (
      <div className="th-loading-screen">
        <div className="th-spinner" />
        <p>Loading destination details...</p>
      </div>
    );
  }

  const locationCleanName = formatLocationTitle(location.name);

  return (
    <>
      <Helmet>
        <title>Private Theatres in {locationCleanName} | Carnival Castle</title>
        <meta
          name="description"
          content={`Browse and reserve private celebration theatres at Carnival Castle ${locationCleanName}. 4K visual projection, custom decor, and private dining.`}
        />
      </Helmet>

      <div className="th-page-root">
        <Header />

        <div className="th-main-container">
          {/* ── Subnav Back Link ── */}
          <div className="th-subnav">
            <button
              type="button"
              className="th-back-link"
              onClick={() => navigate("/locations")}
            >
              <ArrowLeft size={13} /> All Theatres
            </button>
          </div>

          {/* ── Compact Editorial Header ── */}
          <header className="th-compact-header">
            <span className="th-eyebrow">✦ PRIVATE THEATRE COLLECTION ✦</span>
            <h1 className="th-location-title">{locationCleanName}</h1>
            <p className="th-location-subtitle">
              Choose the theatre that fits your celebration · {location.landmark || location.city}
            </p>
          </header>

          {/* ── Sleek Reservation Date Control Bar ── */}
          <div className="th-reservation-bar">
            <div className="th-res-control" onClick={openCalendar} role="button" tabIndex={0}>
              <span className="th-res-label">SELECT DATE</span>
              <div className="th-res-date-box">
                <CalendarIcon size={14} className="th-res-icon" />
                <span className="th-res-date-val">{formatDisplayDate(date)}</span>
                <input
                  type="date"
                  ref={dateRef}
                  className="th-hidden-input"
                  value={date}
                  min={getTodayDateString()}
                  onChange={handleDateChange}
                />
              </div>
            </div>

            <div className="th-res-divider" />

            <div className="th-res-food-note">
              <Utensils size={13} className="th-res-icon" />
              <span>Food & Beverages Available</span>
            </div>
          </div>

          {/* ── Subtle Price Progression Indicator ── */}
          <div className="th-progression-bar">
            <div className="th-prog-left">
              <span className="th-prog-tag">CURATED BY PRICE</span>
              <span className="th-prog-hint">Ascending from entry to luxury private theatres</span>
            </div>

            <div className="th-prog-steps">
              <div className="th-step-item">
                <span className="th-step-code">01</span>
                <span className="th-step-title">ENTRY</span>
                <span className="th-step-price">From ₹{minPrice.toLocaleString("en-IN")}</span>
              </div>
              <span className="th-step-arrow">→</span>
              <div className="th-step-item">
                <span className="th-step-code">02</span>
                <span className="th-step-title">SIGNATURE</span>
                <span className="th-step-price">From ₹{midPrice.toLocaleString("en-IN")}</span>
              </div>
              <span className="th-step-arrow">→</span>
              <div className="th-step-item premium">
                <span className="th-step-code">03</span>
                <span className="th-step-title">PREMIUM</span>
                <span className="th-step-price">From ₹{maxPrice.toLocaleString("en-IN")}</span>
              </div>
            </div>
          </div>

          {/* ── Compact 3-Column Theatre Cards Grid ── */}
          {sortedTheaters.length > 0 ? (
            <div className="th-cards-grid">
              {sortedTheaters.map((data, i) => {
                const tier = getTheatreTier(i, sortedTheaters.length);
                const currentSelectedSlot = selectedSlot[i];
                const theaterCapacities = {
                  "Iris Theatre": 20,
                  "Joy Theatre": 20,
                  "Ruby Theatre": 2,
                  "Vibe Theatres": 15,
                  "Carnival Den": 15,
                  "Amora Theatre": 6,
                };
                const maxPeople = theaterCapacities[data.name] || data.maxPeople || 4;

                const displayPrice = currentSelectedSlot
                  ? currentSelectedSlot.duration === "1:30 hr"
                    ? data.oneandhalfslotPrice || data.offerPrice
                    : currentSelectedSlot.offerPrice ?? data.offerPrice
                  : data.offerPrice;

                const extraPersonRate =
                  currentSelectedSlot && currentSelectedSlot.duration === "1:30 hr"
                    ? data.onehalfanhourExtraPersonPrice || data.extraPersonprice
                    : data.extraPersonprice;

                const isAvailable = data.availableSlotsCount > 0;

                return (
                  <article key={data._id || i} className="th-theatre-card">
                    {/* Top Image Area (Approx 38-42% of Card) */}
                    <div className="th-card-media-wrap">
                      <img
                        src={getImageSrc(data)}
                        alt={data.name}
                        className="th-card-image"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "/img/about.jpg";
                        }}
                      />

                      {/* Top Badges */}
                      <div className="th-overlay-badges">
                        <span className="th-tier-badge">
                          {tier.number} · {tier.tier}
                        </span>

                        <span
                          className={`th-status-pill ${
                            isAvailable ? "status-avail" : "status-sold"
                          }`}
                        >
                          <span className="th-dot" />
                          {isAvailable
                            ? `${data.availableSlotsCount} slots available`
                            : "Sold Out"}
                        </span>
                      </div>

                      {/* Understated Video Preview Button */}
                      {data.link && (
                        <button
                          type="button"
                          className="th-preview-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenModal(data.link);
                          }}
                        >
                          <Play size={10} fill="#FFFFFF" /> Preview
                        </button>
                      )}
                    </div>

                    {/* Compact Card Body */}
                    <div className="th-card-body">
                      {/* Line 1 & Line 2: Name, Location, Price & Capacity */}
                      <div className="th-header-row">
                        <div className="th-title-group">
                          <h3 className="th-name">{data.name}</h3>
                          <div className="th-loc-cap-line">
                            <span className="th-loc-item">
                              <MapPin size={11} className="th-icon-gold" /> {locationCleanName}
                            </span>
                            <span className="th-bullet">·</span>
                            <span className="th-cap-item">Up to {maxPeople} guests</span>
                          </div>
                        </div>

                        <div className="th-price-group">
                          <div className="th-price-val">
                            ₹{displayPrice?.toLocaleString("en-IN")}
                          </div>
                          <div className="th-price-unit">
                            for up to {data.maxPeople} guests
                          </div>
                        </div>
                      </div>

                      {/* Compact Metadata Strip */}
                      <div className="th-features-strip">
                        <span className="th-feat-tag">
                          <Film size={11} className="th-icon-gold" /> Private Screen
                        </span>
                        <span className="th-feat-dot">·</span>
                        <span className="th-feat-tag">
                          <Sparkles size={11} className="th-icon-gold" /> Ambient Decor
                        </span>
                        <span className="th-feat-dot">·</span>
                        <span className="th-feat-tag">
                          <Users size={11} className="th-icon-gold" /> Max {data.maxSeating} Seating
                        </span>
                        <span className="th-feat-dot">·</span>
                        <span className="th-feat-tag">
                          <Utensils size={11} className="th-icon-gold" /> Food Available
                        </span>
                      </div>

                      {/* Extra Guest Discreet Note */}
                      {extraPersonRate > 0 && (
                        <div className="th-extra-charge-line">
                          + ₹{extraPersonRate} per additional guest
                        </div>
                      )}

                      {/* Compact Time-Slot Section */}
                      <div className="th-slots-block">
                        <div className="th-slots-header">
                          <span className="th-slots-label">SELECT SHOW TIME</span>
                          {currentSelectedSlot ? (
                            <span className="th-slots-selected-text">
                              {convertTo12HourFormat(currentSelectedSlot.fromTime)} -{" "}
                              {convertTo12HourFormat(currentSelectedSlot.toTime)}
                              {slotHasFreeAddon(currentSelectedSlot) && (
                                <span className="th-header-free-badge"> · 🎁 Free Add-On</span>
                              )}
                            </span>
                          ) : (
                            <span className="th-slots-hint">Choose your slot</span>
                          )}
                        </div>

                        {/* Premium Free Add-On Indicator */}
                        <div className="th-addon-banner">
                          <div className="th-addon-banner-main">
                            <span className="th-addon-gift-icon">🎁</span>
                            <span className="th-addon-bold-text">FREE ADD-ON</span>
                            <span className="th-addon-sep-dot">·</span>
                            <span className="th-addon-timing-text">Morning & Afternoon slots only</span>
                          </div>
                        </div>

                        {/* Single-Row Horizontal Scrollable Slots (Square Boxes) */}
                        <div className="th-slots-grid">
                          {data.availableSlots?.map((slot, sIdx) => {
                            const from12 = convertTo12HourFormat(slot.fromTime);
                            const to12 = convertTo12HourFormat(slot.toTime);
                            const duration = calculateDuration(slot.fromTime, slot.toTime);
                            const isSelected =
                              currentSelectedSlot && currentSelectedSlot._id === slot._id;
                            const hasFreeAddon = slotHasFreeAddon(slot);

                            let discountText = null;
                            if (
                              duration === "1:30 hr" &&
                              data.offerPrice &&
                              data.oneandhalfslotPrice
                            ) {
                              const diff = data.offerPrice - data.oneandhalfslotPrice;
                              if (diff > 0) discountText = `₹${diff} less`;
                            }

                            return (
                              <button
                                key={slot._id || sIdx}
                                type="button"
                                disabled={slot.isDisabled}
                                className={`th-slot-pill ${
                                  isSelected ? "active" : ""
                                } ${slot.isDisabled ? "disabled" : ""} ${
                                  hasFreeAddon && !slot.isDisabled ? "has-addon" : ""
                                }`}
                                onClick={(e) => handleSlot(e, { ...slot, duration }, i)}
                                title={`${from12} – ${to12}${discountText ? ` (${discountText})` : ""}${hasFreeAddon ? " · Free Add-On" : ""}`}
                              >
                                <span className="th-sq-time">{from12}</span>
                                <span className="th-sq-sep">–</span>
                                <span className="th-sq-time">{to12}</span>
                                {discountText && !slot.isDisabled ? (
                                  <span className="th-sq-disc">{discountText}</span>
                                ) : hasFreeAddon && !slot.isDisabled ? (
                                  <span className="th-sq-addon">
                                    <span className="th-sq-addon-icon">🎁</span> FREE
                                  </span>
                                ) : null}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Full-Width Booking Button */}
                      <div className="th-action-wrap">
                        <button
                          type="button"
                          className="th-book-cta"
                          disabled={!currentSelectedSlot}
                          title={!currentSelectedSlot ? "Please select a slot first" : ""}
                          onClick={() => handleBasicPlan(data, i)}
                        >
                          {currentSelectedSlot ? (
                            <>
                              Book This Theatre <ArrowRight size={14} className="th-btn-arrow" />
                            </>
                          ) : (
                            "Select a Slot to Book"
                          )}
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="th-empty-box">
              <p>No theatres available for {locationCleanName} on this date.</p>
              <button
                type="button"
                className="th-btn-empty"
                onClick={() => navigate("/locations")}
              >
                Explore Other Locations
              </button>
            </div>
          )}

          {/* ── Compact Other Locations Switcher ── */}
          {addresses.length > 1 && (
            <div className="th-switch-venues">
              <div className="th-sv-title">
                ✦ EXPLORE OTHER THEATRES:
              </div>
              <div className="th-sv-pills">
                {addresses
                  .filter((a) => a._id !== location._id)
                  .map((addr) => {
                    const clean = formatLocationTitle(addr.name);
                    const slugName = addr.name.toLowerCase().replace(/\s+/g, "-");
                    return (
                      <button
                        key={addr._id}
                        type="button"
                        className="th-sv-pill"
                        onClick={() => {
                          sessionStorage.setItem("selectedAddress", JSON.stringify(addr));
                          navigate(`/theaters/${slugName}`, { state: { address: addr } });
                        }}
                      >
                        <MapPin size={11} className="th-icon-gold" /> {clean}
                      </button>
                    );
                  })}
              </div>
            </div>
          )}
        </div>

        {/* ── 90-Minute Slot Notification Modal ── */}
        <Modal show={modalPop} onHide={() => setModalPop(false)} centered size="sm">
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title style={{ fontFamily: "'Playfair Display', serif", fontSize: 18 }}>
              Special Slot Duration
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="pt-2 text-center">
            <p style={{ color: "#554D5D", fontSize: 13, lineHeight: 1.5, margin: "0 0 14px" }}>
              You have selected an exclusive 1.5 hours celebration slot and will be charged accordingly.
            </p>
            <button
              type="button"
              className="th-btn-modal"
              onClick={() => setModalPop(false)}
            >
              Continue Booking
            </button>
          </Modal.Body>
        </Modal>

        {/* ── Video Preview Modal ── */}
        {showModal && (
          <div className="th-video-backdrop" onClick={handleCloseModal}>
            <div className="th-video-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="th-video-head">
                <span className="th-video-title">Theatre Preview</span>
                <button type="button" className="th-video-close" onClick={handleCloseModal}>
                  <X size={16} />
                </button>
              </div>
              <div className="th-iframe-ratio">
                <iframe
                  src={embedUrl}
                  title="Theatre Video Preview"
                  allowFullScreen
                  className="th-iframe"
                />
              </div>
            </div>
          </div>
        )}

        <ToastContainer />
        <Footer />
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,500;0,700;0,800;1,500&family=Inter:wght@400;500;600;700&display=swap');

        /* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
           BASE PAGE — Deep luxury parchment
        ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
        .th-page-root {
          background: #F7F3EE;
          background-image:
            radial-gradient(ellipse 60% 40% at 50% 0%, rgba(94,14,115,0.04) 0%, transparent 70%),
            radial-gradient(ellipse 80% 50% at 80% 100%, rgba(201,168,76,0.05) 0%, transparent 60%);
          color: #1A0030;
          min-height: 100vh;
          font-family: 'Inter', system-ui, sans-serif;
        }

        .th-main-container {
          max-width: 1380px;
          width: calc(100% - 48px);
          margin: 0 auto;
          padding: 18px 0 72px;
        }

        /* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
           SUBNAV
        ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
        .th-subnav { margin-bottom: 16px; }

        .th-back-link {
          background: transparent;
          border: none;
          color: #5E0E73;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.3px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          cursor: pointer;
          padding: 5px 10px;
          border-radius: 8px;
          transition: background 0.2s, color 0.2s;
        }
        .th-back-link:hover {
          background: rgba(94,14,115,0.07);
          color: #1A0030;
        }

        /* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
           EDITORIAL HEADER
        ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
        .th-compact-header {
          text-align: center;
          margin-bottom: 28px;
          padding-top: 8px;
        }
        .th-eyebrow {
          display: inline-block;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 3px;
          color: #C9A84C;
          text-transform: uppercase;
          margin-bottom: 8px;
          padding: 3px 14px;
          border: 1px solid rgba(201,168,76,0.35);
          border-radius: 100px;
          background: rgba(201,168,76,0.06);
        }
        .th-location-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: clamp(30px, 4vw, 46px);
          font-weight: 800;
          color: #0D001A;
          line-height: 1.1;
          margin: 0 0 8px;
          letter-spacing: -0.5px;
        }
        .th-location-subtitle {
          font-size: 14px;
          color: #7A6E85;
          margin: 0 auto;
          max-width: 520px;
          line-height: 1.6;
        }

        /* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
           DATE CONTROL BAR
        ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
        .th-reservation-bar {
          background: #FFFFFF;
          border: 1px solid #E2D8CC;
          border-radius: 14px;
          padding: 10px 22px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 22px;
          max-width: 580px;
          margin: 0 auto 28px;
          box-shadow: 0 2px 16px rgba(26,0,48,0.05);
        }
        .th-res-control {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          cursor: pointer;
        }
        .th-res-label {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 1.5px;
          color: #0D001A;
          text-transform: uppercase;
        }
        .th-res-date-box {
          position: relative;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #F8F3EC;
          border: 1.5px solid #C9A84C;
          border-radius: 10px;
          padding: 7px 14px;
          transition: background 0.2s;
        }
        .th-res-date-box:hover { background: #F2EAE0; }
        .th-res-date-val {
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 0.4px;
          color: #1A0030;
        }
        .th-hidden-input {
          position: absolute;
          opacity: 0;
          pointer-events: none;
          width: 0;
          height: 0;
        }
        .th-res-divider {
          width: 1px;
          height: 26px;
          background: #EAE2D5;
        }
        .th-res-food-note {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 12.5px;
          color: #7A6E85;
          font-weight: 500;
        }
        .th-res-icon { color: #C9A84C; }

        /* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
           PRICE PROGRESSION BAR
        ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
        .th-progression-bar {
          background: #FFFFFF;
          border: 1px solid #E2D8CC;
          border-radius: 14px;
          padding: 14px 24px;
          margin-bottom: 32px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 14px;
          box-shadow: 0 2px 12px rgba(26,0,48,0.03);
        }
        .th-prog-left { display: flex; flex-direction: column; gap: 2px; }
        .th-prog-tag {
          font-size: 9.5px;
          font-weight: 700;
          letter-spacing: 2px;
          color: #C9A84C;
          text-transform: uppercase;
        }
        .th-prog-hint { font-size: 12px; color: #8C849A; }
        .th-prog-steps { display: flex; align-items: center; gap: 10px; }
        .th-step-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 2px;
          background: #FAF6F1;
          border: 1px solid #E5D9C9;
          border-radius: 10px;
          padding: 7px 16px;
        }
        .th-step-item.premium {
          border-color: #C9A84C;
          background: linear-gradient(135deg, #FFFDF6 0%, #FFF8E7 100%);
        }
        .th-step-code { font-size: 9px; font-weight: 700; color: #C9A84C; letter-spacing: 0.5px; }
        .th-step-title { font-size: 10.5px; font-weight: 700; letter-spacing: 0.5px; color: #1A0030; }
        .th-step-price { font-size: 11px; font-weight: 600; color: #5E0E73; }
        .th-step-arrow { color: #C9A84C; font-size: 14px; font-weight: 600; }

        /* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
           CARDS GRID
        ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
        .th-cards-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 28px;
          margin-bottom: 44px;
        }

        /* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
           THEATRE CARD — PREMIUM LUXURY DESIGN
        ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
        .th-theatre-card {
          background: #FFFFFF;
          border: 1px solid rgba(220,208,196,0.8);
          border-radius: 20px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          position: relative;
          transition:
            transform 0.38s cubic-bezier(0.16,1,0.3,1),
            box-shadow 0.38s cubic-bezier(0.16,1,0.3,1),
            border-color 0.3s ease;
          box-shadow:
            0 1px 2px rgba(0,0,0,0.04),
            0 4px 24px rgba(26,0,48,0.06);
        }

        /* Shimmering gold top edge */
        .th-theatre-card::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 2.5px;
          background: linear-gradient(90deg,
            transparent 0%,
            #C9A84C 20%,
            #F5D87A 50%,
            #C9A84C 80%,
            transparent 100%);
          z-index: 5;
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        .th-theatre-card:hover::before { opacity: 1; }

        .th-theatre-card:hover {
          transform: translateY(-6px);
          border-color: rgba(201,168,76,0.5);
          box-shadow:
            0 0 0 1px rgba(201,168,76,0.2),
            0 12px 40px rgba(26,0,48,0.12),
            0 4px 16px rgba(201,168,76,0.12);
        }

        /* ── Image Area ── */
        .th-card-media-wrap {
          position: relative;
          height: 220px;
          background: #0D001A;
          overflow: hidden;
          flex-shrink: 0;
        }
        .th-card-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 0.6s cubic-bezier(0.16,1,0.3,1);
          filter: brightness(0.92);
        }
        .th-theatre-card:hover .th-card-image {
          transform: scale(1.07);
          filter: brightness(1);
        }

        /* Gradient scrim over image bottom */
        .th-card-media-wrap::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(
            to bottom,
            transparent 30%,
            rgba(13,0,26,0.45) 100%
          );
          pointer-events: none;
          z-index: 2;
        }

        /* Overlay badges */
        .th-overlay-badges {
          position: absolute;
          top: 12px;
          left: 12px;
          right: 12px;
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 8px;
          pointer-events: none;
          z-index: 3;
        }

        /* Tier badge — obsidian glass */
        .th-tier-badge {
          background: linear-gradient(135deg,
            rgba(10,0,20,0.88) 0%,
            rgba(40,4,64,0.88) 100%);
          color: #F5D87A;
          font-size: 9px;
          font-weight: 800;
          letter-spacing: 1.2px;
          text-transform: uppercase;
          padding: 4px 12px;
          border-radius: 100px;
          border: 1px solid rgba(245,216,122,0.4);
          backdrop-filter: blur(12px);
          box-shadow: 0 2px 12px rgba(0,0,0,0.3);
        }

        /* Status pill */
        .th-status-pill {
          background: rgba(255,255,255,0.95);
          backdrop-filter: blur(12px);
          border-radius: 100px;
          padding: 4px 11px;
          font-size: 9px;
          font-weight: 800;
          letter-spacing: 0.5px;
          display: inline-flex;
          align-items: center;
          gap: 5px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.14);
        }
        .th-status-pill.status-avail {
          color: #166B22;
          border: 1px solid rgba(22,107,34,0.2);
        }
        .th-status-pill.status-sold {
          color: #B71C1C;
          border: 1px solid rgba(183,28,28,0.2);
        }
        .th-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: currentColor;
          animation: th-pulse-dot 2s ease-in-out infinite;
          flex-shrink: 0;
        }
        .th-status-pill.status-sold .th-dot { animation: none; }
        @keyframes th-pulse-dot {
          0%, 100% { opacity: 1; box-shadow: 0 0 0 0 currentColor; }
          50% { opacity: 0.7; box-shadow: 0 0 0 3px rgba(22,107,34,0); }
        }

        /* Preview button */
        .th-preview-btn {
          position: absolute;
          bottom: 12px;
          left: 12px;
          background: rgba(13,0,26,0.82);
          backdrop-filter: blur(10px);
          color: #F5D87A;
          border: 1px solid rgba(245,216,122,0.4);
          border-radius: 100px;
          padding: 5px 13px;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.3px;
          display: inline-flex;
          align-items: center;
          gap: 5px;
          cursor: pointer;
          transition: all 0.22s ease;
          z-index: 3;
        }
        .th-preview-btn:hover {
          background: rgba(94,14,115,0.9);
          border-color: #F5D87A;
          transform: translateY(-1px);
          box-shadow: 0 4px 14px rgba(245,216,122,0.25);
        }

        /* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
           CARD BODY
        ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
        .th-card-body {
          padding: 18px 20px 20px;
          display: flex;
          flex-direction: column;
          flex: 1;
          gap: 0;
        }

        /* Name + price row */
        .th-header-row {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 10px;
          margin-bottom: 10px;
        }
        .th-title-group { min-width: 0; flex: 1; }
        .th-name {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 19px;
          font-weight: 700;
          color: #0D001A;
          line-height: 1.2;
          margin: 0 0 4px;
          letter-spacing: -0.2px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .th-loc-cap-line {
          font-size: 11.5px;
          color: #8C849A;
          display: flex;
          align-items: center;
          gap: 5px;
          font-weight: 500;
        }
        .th-icon-gold { color: #C9A84C; flex-shrink: 0; }
        .th-bullet { color: #CCC4D4; }
        .th-price-group { text-align: right; flex-shrink: 0; }
        .th-price-val {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 22px;
          font-weight: 800;
          color: #4E0A63;
          line-height: 1;
          letter-spacing: -0.3px;
        }
        .th-price-unit {
          font-size: 10px;
          color: #9C94A8;
          margin-top: 3px;
          font-weight: 500;
        }

        /* Features strip */
        .th-features-strip {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 4px 0;
          padding: 8px 12px;
          background: linear-gradient(135deg, #FAF7F2 0%, #F5F0E8 100%);
          border: 1px solid #EAE0D2;
          border-radius: 10px;
          margin-bottom: 10px;
        }
        .th-feat-tag {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 10.5px;
          font-weight: 600;
          color: #3D3347;
          white-space: nowrap;
        }
        .th-feat-dot { color: #C8BEAF; margin: 0 5px; font-size: 12px; }

        /* Extra charge */
        .th-extra-charge-line {
          font-size: 10px;
          color: #9C94A8;
          font-weight: 500;
          margin-bottom: 10px;
          padding-left: 2px;
        }

        /* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
           FREE ADD-ON BANNER
        ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
        .th-addon-banner {
          display: flex;
          align-items: center;
          gap: 0;
          background: linear-gradient(135deg, #FFF9ED 0%, #FFF3D4 100%);
          border: 1px solid #E8CC85;
          border-radius: 9px;
          padding: 7px 12px;
          margin-bottom: 10px;
        }
        .th-addon-banner-main {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
          font-size: 10.5px;
        }
        .th-addon-gift-icon { font-size: 12px; line-height: 1; }
        .th-addon-bold-text {
          font-size: 9.5px;
          font-weight: 800;
          letter-spacing: 0.8px;
          color: #6D3A00;
          text-transform: uppercase;
        }
        .th-addon-sep-dot { color: #C9A84C; font-weight: 700; }
        .th-addon-timing-text { font-size: 10px; font-weight: 500; color: #7A5A20; }

        /* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
           SLOT SECTION
        ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
        .th-slots-block { margin-bottom: 14px; }

        .th-slots-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 8px;
          padding: 0 1px;
        }
        .th-slots-label {
          font-size: 9px;
          font-weight: 800;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          color: #4E0A63;
        }
        .th-slots-selected-text {
          font-size: 10px;
          font-weight: 700;
          color: #5E0E73;
        }
        .th-header-free-badge { color: #C9A84C; font-weight: 800; }
        .th-slots-hint { font-size: 10px; color: #9C94A8; font-weight: 500; }

        /* Slots container — Single horizontal line (one row, scrollable) */
        .th-slots-grid {
          display: flex;
          align-items: center;
          gap: 8px;
          overflow-x: auto;
          overflow-y: hidden;
          padding: 4px 2px 10px 2px;
          width: 100%;
          scrollbar-width: thin;
          scrollbar-color: #D6C8A8 transparent;
          -webkit-overflow-scrolling: touch;
        }
        .th-slots-grid::-webkit-scrollbar {
          height: 4px;
        }
        .th-slots-grid::-webkit-scrollbar-track {
          background: rgba(220, 210, 195, 0.25);
          border-radius: 4px;
        }
        .th-slots-grid::-webkit-scrollbar-thumb {
          background: #D0C2A0;
          border-radius: 4px;
        }
        .th-slots-grid::-webkit-scrollbar-thumb:hover {
          background: #C9A84C;
        }

        /* Square box for each timing slot */
        .th-slot-pill {
          flex: 0 0 66px;
          width: 66px;
          height: 66px;
          min-width: 66px;
          min-height: 66px;
          aspect-ratio: 1 / 1;
          background: #FAF7F2;
          border: 1px solid #DDD5C5;
          border-radius: 8px;
          padding: 4px 2px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          cursor: pointer;
          transition:
            background 0.2s ease,
            border-color 0.2s ease,
            transform 0.2s ease,
            box-shadow 0.2s ease;
          box-sizing: border-box;
          text-align: center;
          position: relative;
        }
        .th-slot-pill.has-addon {
          background: #FEFCF6;
          border-color: #DDD0A8;
        }
        .th-sq-time {
          font-size: 9.5px;
          font-weight: 700;
          color: #2A1238;
          line-height: 1.15;
          white-space: nowrap;
        }
        .th-sq-sep {
          font-size: 8px;
          color: #9C94A8;
          line-height: 0.9;
          margin: 1px 0;
        }
        .th-sq-disc {
          font-size: 7.5px;
          color: #1B6B20;
          font-weight: 800;
          margin-top: 2px;
          line-height: 1;
          white-space: nowrap;
        }
        .th-sq-addon {
          font-size: 7.5px;
          font-weight: 800;
          color: #8C6A1E;
          letter-spacing: 0.2px;
          margin-top: 2px;
          line-height: 1;
          display: inline-flex;
          align-items: center;
          gap: 2px;
          white-space: nowrap;
        }
        .th-sq-addon-icon { font-size: 7.5px; }

        .th-slot-pill:hover:not(:disabled) {
          background: #FFFFFF;
          border-color: #C9A84C;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(201,168,76,0.18);
        }
        .th-slot-pill.active {
          background: linear-gradient(135deg, #240044 0%, #0E001C 100%) !important;
          border-color: #F5D87A !important;
          box-shadow:
            0 4px 18px rgba(13,0,26,0.35),
            0 0 0 1px rgba(245,216,122,0.3);
          transform: translateY(-1px);
        }
        .th-slot-pill.active .th-sq-time {
          color: #FFFFFF;
          font-weight: 800;
        }
        .th-slot-pill.active .th-sq-sep {
          color: rgba(255, 255, 255, 0.7);
        }
        .th-slot-pill.active .th-sq-addon { color: #F5D87A; }
        .th-slot-pill.active .th-sq-disc { color: #7AE87D; }

        .th-slot-pill.disabled {
          background: #F5F0EB;
          border-color: #E8E0D8;
          cursor: not-allowed;
          opacity: 0.42;
        }
        .th-slot-pill.disabled .th-sq-time {
          color: #B0A8B8;
          text-decoration: line-through;
        }
        .th-slot-pill.disabled .th-sq-sep {
          color: #B0A8B8;
        }

        /* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
           BOOKING CTA
        ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
        .th-action-wrap { margin-top: auto; padding-top: 4px; }

        .th-book-cta {
          width: 100%;
          height: 46px;
          background: linear-gradient(135deg, #350057 0%, #130022 100%);
          color: #FFFFFF;
          border: 1.5px solid rgba(245,216,122,0.6);
          border-radius: 12px;
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 0.3px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          cursor: pointer;
          box-shadow:
            0 4px 20px rgba(53,0,87,0.25),
            inset 0 1px 0 rgba(255,255,255,0.08);
          transition: all 0.3s cubic-bezier(0.16,1,0.3,1);
          position: relative;
          overflow: hidden;
        }
        .th-book-cta::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg,
            rgba(245,216,122,0.12) 0%,
            transparent 60%);
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        .th-book-cta:hover::before { opacity: 1; }
        .th-book-cta:hover {
          background: linear-gradient(135deg, #5E0E73 0%, #280040 100%);
          border-color: #F5D87A;
          transform: translateY(-2px);
          box-shadow:
            0 10px 32px rgba(94,14,115,0.38),
            0 0 20px rgba(245,216,122,0.2),
            inset 0 1px 0 rgba(255,255,255,0.12);
        }
        .th-btn-arrow { transition: transform 0.22s ease; }
        .th-book-cta:hover:not(:disabled) .th-btn-arrow { transform: translateX(5px); }

        /* Disabled state */
        .th-book-cta:disabled {
          background: #E8E2ED;
          border-color: #D8D0DF;
          color: #9E96A8;
          box-shadow: none;
          cursor: not-allowed;
          transform: none;
        }
        .th-book-cta:disabled::before { display: none; }
        .th-book-cta:hover:disabled { transform: none; box-shadow: none; }

        /* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
           OTHER LOCATIONS SWITCHER
        ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
        .th-switch-venues {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 14px;
          flex-wrap: wrap;
          padding: 18px 24px;
          background: #FFFFFF;
          border: 1px solid #E2D8CC;
          border-radius: 14px;
          font-size: 12px;
          box-shadow: 0 2px 12px rgba(26,0,48,0.03);
        }
        .th-sv-title {
          font-size: 9.5px;
          font-weight: 700;
          letter-spacing: 1.5px;
          color: #C9A84C;
          text-transform: uppercase;
        }
        .th-sv-pills { display: flex; gap: 8px; flex-wrap: wrap; }
        .th-sv-pill {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          background: #FAF6F1;
          border: 1px solid #DDD5C5;
          border-radius: 10px;
          padding: 6px 16px;
          font-size: 12px;
          font-weight: 600;
          color: #1A0030;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .th-sv-pill:hover {
          background: #1A0030;
          color: #F5D87A;
          border-color: #1A0030;
        }

        /* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
           EMPTY STATE
        ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
        .th-empty-box {
          background: #FFFFFF;
          border: 1.5px dashed #D8CCBE;
          border-radius: 16px;
          padding: 48px 24px;
          text-align: center;
          color: #7A6E85;
          margin-bottom: 32px;
        }
        .th-btn-empty {
          margin-top: 14px;
          background: #1A0030;
          color: #FFFFFF;
          border: 1px solid #C9A84C;
          border-radius: 10px;
          padding: 9px 22px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s;
        }
        .th-btn-empty:hover { background: #350057; }

        /* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
           LOADING SCREEN
        ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
        .th-loading-screen {
          min-height: 80vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 14px;
          color: #7A6E85;
          font-size: 14px;
          background: #F7F3EE;
          font-family: 'Inter', sans-serif;
        }
        .th-spinner {
          width: 34px;
          height: 34px;
          border: 3px solid #E5DCD0;
          border-top-color: #5E0E73;
          border-radius: 50%;
          animation: th-spin 0.75s linear infinite;
        }
        @keyframes th-spin { to { transform: rotate(360deg); } }

        /* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
           MODAL BUTTONS
        ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
        .th-btn-modal {
          background: linear-gradient(135deg, #350057 0%, #130022 100%);
          color: #FFFFFF;
          border: 1px solid #C9A84C;
          border-radius: 10px;
          padding: 9px 24px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
        }

        /* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
           VIDEO MODAL
        ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
        .th-video-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(10,0,18,0.8);
          backdrop-filter: blur(6px);
          z-index: 2000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }
        .th-video-dialog {
          background: #120020;
          border-radius: 16px;
          max-width: 760px;
          width: 100%;
          overflow: hidden;
          border: 1px solid rgba(201,168,76,0.3);
          box-shadow: 0 24px 60px rgba(0,0,0,0.5);
        }
        .th-video-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 18px;
          border-bottom: 1px solid rgba(255,255,255,0.08);
        }
        .th-video-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 16px;
          font-weight: 700;
          color: #FFFFFF;
        }
        .th-video-close {
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.7);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: color 0.2s;
        }
        .th-video-close:hover { color: #FFFFFF; }
        .th-iframe-ratio {
          position: relative;
          width: 100%;
          padding-top: 56.25%;
        }
        .th-iframe {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          border: none;
        }

        /* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
           RESPONSIVE
        ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
        @media (max-width: 1200px) {
          .th-cards-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 22px;
          }
        }

        @media (max-width: 768px) {
          .th-main-container {
            width: calc(100% - 28px);
            padding: 14px 0 48px;
          }
          .th-cards-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }
          .th-card-media-wrap { height: 200px; }
          .th-progression-bar {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }
          .th-prog-steps {
            width: 100%;
            justify-content: space-between;
          }
          .th-reservation-bar {
            flex-direction: column;
            gap: 10px;
            padding: 12px 16px;
          }
          .th-res-divider { display: none; }
          .th-header-row { flex-direction: column; gap: 6px; }
          .th-price-group { text-align: left; }
          .th-features-strip { justify-content: flex-start; gap: 4px 0; }
          /* Keep single horizontal row on tablet & mobile */
          .th-slots-grid {
            gap: 7px;
          }
        }

        @media (max-width: 440px) {
          .th-slots-grid {
            gap: 6px;
          }
        }
      `}</style>
    </>
  );
}